/**
 * ListAggregatesFooter — sticky Σ summary under a list / tabular grid.
 * "+ Σ" menu is grouped by field with Switch per op; layout is Horizontal
 * (inline chips) or Vertical (one field+op+value per stacked line).
 */
import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import Divider from '@mui/material/Divider'
import Switch from '@mui/material/Switch'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { monoSx } from '@/shared/listCell'
import { MicroLabel } from '@/shared/operator'
import { MORE_PIN_GRID } from '@/shared/components/CommandBar'
import {
  AGGREGATE_OPS,
  formatAggregateValue,
  mergeAggregatesFieldOrder,
  moveAggregatesField,
  orderAggregatePairs,
  parseAggregatePair,
  sanitizeAggregatesLayout,
  toggleAggregatePair,
  type AggregatesLayout,
} from '@/shared/listViews/aggregatesLayout'

export type { AggregatesLayout, AggregateOp } from '@/shared/listViews/aggregatesLayout'
export { sanitizeAggregatesLayout, AGGREGATE_OPS }

export interface ListAggregatesFooterNumericField {
  name: string
  label: string
  precision?: number
  scale?: number
}

export interface ListAggregatesFooterProps {
  recordCount: number
  aggregates: string[]
  values: Record<string, number | string | null>
  numericFields: ListAggregatesFooterNumericField[]
  onChange: (next: string[]) => void
  layout?: AggregatesLayout
  onLayoutChange?: (next: AggregatesLayout) => void
  /** Show the "Table" layout (values under matching columns). Tabular grids only. */
  columnLayout?: boolean
  /** User field order; missing/unknown names fall back to `numericFields` (column LTR). */
  fieldOrder?: string[]
  onFieldOrderChange?: (next: string[]) => void
  scopeOverride?: { count: number; values: Record<string, number | null> }
}

export const ListAggregatesFooter: React.FC<ListAggregatesFooterProps> = ({
  recordCount,
  aggregates,
  values,
  numericFields,
  onChange,
  layout: layoutProp,
  onLayoutChange,
  columnLayout = false,
  fieldOrder: fieldOrderProp,
  onFieldOrderChange,
  scopeOverride,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const layout = sanitizeAggregatesLayout(layoutProp)
  const columnOrder = numericFields.map((f) => f.name)
  const fieldOrder = mergeAggregatesFieldOrder(columnOrder, fieldOrderProp)
  const orderedFields = fieldOrder
    .map((name) => numericFields.find((f) => f.name === name))
    .filter((f): f is ListAggregatesFooterNumericField => Boolean(f))
  const orderedAggregates = orderAggregatePairs(aggregates, fieldOrder)

  const labelFor = (field: string) => numericFields.find((f) => f.name === field)?.label ?? field

  const toggle = (pair: string) => {
    onChange(toggleAggregatePair(aggregates, pair, fieldOrder))
  }

  const reorderField = (from: string, to: string) => {
    const next = moveAggregatesField(fieldOrder, from, to)
    if (next === fieldOrder || next.join('\0') === fieldOrder.join('\0')) return
    onFieldOrderChange?.(next)
    onChange(orderAggregatePairs(aggregates, next))
  }

  const effValues = scopeOverride ? scopeOverride.values : values
  const countLabel = scopeOverride
    ? `${scopeOverride.count.toLocaleString()} selected`
    : `${recordCount.toLocaleString()} records`

  const showChips = layout !== 'columns'

  const pairParts = (pair: string) => {
    const { field, op } = parseAggregatePair(pair)
    return {
      field,
      op,
      label: `${labelFor(field)} ${op}:`,
      value: formatAggregateValue(effValues[pair], numericFields.find((f) => f.name === field), op),
    }
  }

  const valueChip = (pair: string) => {
    const { label, value } = pairParts(pair)
    return (
      <Box key={pair} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, whiteSpace: 'nowrap' }}>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{label}</Typography>
        <Typography sx={{ ...monoSx, fontSize: 12, fontWeight: 600 }}>{value}</Typography>
      </Box>
    )
  }

  return (
    <Box
      data-aggregates-layout={layout}
      sx={{
        display: 'flex',
        alignItems: layout === 'vertical' ? 'stretch' : 'center',
        flexDirection: layout === 'vertical' ? 'column' : 'row',
        gap: layout === 'vertical' ? 1 : 2,
        px: 1.5,
        py: 0.75,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        position: 'sticky',
        bottom: 0,
        flexWrap: layout === 'vertical' ? 'nowrap' : 'wrap',
      }}
    >
      {layout === 'vertical' ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <MicroLabel>{countLabel}</MicroLabel>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="text"
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary', minWidth: 'auto', px: 1 }}
            >
              + Σ
            </Button>
          </Box>
          {showChips ? (
            <Box
              data-aggregates-stack
              sx={{
                alignSelf: 'flex-end',
                display: 'grid',
                gridTemplateColumns: 'max-content max-content',
                columnGap: 1,
                rowGap: 1,
                alignItems: 'baseline',
              }}
            >
              {orderedAggregates.map((pair) => {
                const { label, value } = pairParts(pair)
                return (
                  <React.Fragment key={pair}>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {label}
                    </Typography>
                    <Typography
                      data-aggregates-value
                      sx={{ ...monoSx, fontSize: 12, fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {value}
                    </Typography>
                  </React.Fragment>
                )
              })}
            </Box>
          ) : null}
        </>
      ) : (
        <>
          <MicroLabel>{countLabel}</MicroLabel>
          <Box sx={{ flex: 1 }} />
          {showChips ? orderedAggregates.map(valueChip) : null}
          <Button
            variant="text"
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary', minWidth: 'auto', px: 1 }}
          >
            + Σ
          </Button>
        </>
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { minWidth: 240, maxHeight: 420 } } }}
      >
        <Box sx={{ px: 1.5, py: 1 }} onClick={(e) => e.stopPropagation()}>
          <ToggleButtonGroup
            exclusive
            size="small"
            fullWidth
            value={layout}
            onChange={(_, next) => {
              if (next) onLayoutChange?.(next)
            }}
            aria-label="Aggregates layout"
          >
            <ToggleButton value="horizontal" sx={{ textTransform: 'none', fontSize: 12 }}>
              Horizontal
            </ToggleButton>
            <ToggleButton value="vertical" sx={{ textTransform: 'none', fontSize: 12 }}>
              Vertical
            </ToggleButton>
            {columnLayout && (
              <ToggleButton value="columns" sx={{ textTransform: 'none', fontSize: 12 }}>
                Table
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Box>
        <Divider />
        {numericFields.length === 0 && (
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No numeric fields</Typography>
          </Box>
        )}
        {orderedFields.map((f) => (
          <Box
            key={f.name}
            data-testid={`agg-field-${f.name}`}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('text/plain', f.name)
              e.stopPropagation()
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault()
              e.stopPropagation()
              const from = e.dataTransfer.getData('text/plain')
              if (from && from !== f.name) reorderField(from, f.name)
            }}
            sx={{ px: 1.5, py: 0.75 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
              <DragIndicatorIcon
                sx={{ fontSize: 16, color: 'text.disabled', cursor: 'grab' }}
                aria-label={`Reorder ${f.label}`}
              />
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{f.label}</Typography>
            </Box>
            {AGGREGATE_OPS.map((op) => {
              const pair = `${f.name}:${op}`
              return (
                <Box key={pair} sx={MORE_PIN_GRID}>
                  <Typography sx={{ fontSize: 13 }}>{op}</Typography>
                  <Switch
                    size="small"
                    checked={aggregates.includes(pair)}
                    onChange={() => toggle(pair)}
                    slotProps={{ input: { 'aria-label': `${f.label} ${op}` } }}
                    sx={{ justifySelf: 'end' }}
                  />
                </Box>
              )
            })}
          </Box>
        ))}
      </Menu>
    </Box>
  )
}
